import React, { useState } from "react";
import { FileTextOutlined } from "@ant-design/icons";
import { Button, Tooltip, Spin } from "antd";
import { isMobile } from "react-device-detect";
import get from "lodash/get";

import {
  TYPES,
  PLAYER_LEADERBOARD_FIELDS,
  CARD_LEADERBOARD_FIELDS,
  AUCTION_FIELDS,
  MY_COLLECTION_FIELDS,
  MY_COLLECTION_SOLD_FIELDS,
  API_CALL_LOGGING_FIELDS,
  ADMIN_PLAYER_FIELDS,
  ADMIN_SET_FIELDS,
  ADMIN_SET_VARIATION_FIELDS,
  ADMIN_GRADE_FIELDS,
  ADMIN_VARIATION_FIELDS,
  MY_COLLECTION_SEALED_WAX_FIELDS,
  MY_COLLECTION_SEALED_WAX_SOLD_FIELDS,
  MY_COLLECTION_IMPORT_REJECTED_FIELDS,
  ADMIN_CARD_FIELDS,
  ADMIN_CARD_RESULTS_FIELDS,
} from "./layouts/csv.assets";
import Toaster from "services/toaster";

const downloadCsv = async ({
  data,
  asyncDataFn,
  type,
  filename = "market-movers-export.csv",
  delimiter,
  escapeFn,
  ...props
}) => {
  const fields =
    {
      [TYPES.PLAYER_LEADERBOARD]: PLAYER_LEADERBOARD_FIELDS,
      [TYPES.CARD_LEADERBOARD]: CARD_LEADERBOARD_FIELDS,
      [TYPES.AUCTION]: AUCTION_FIELDS,
      [TYPES.MY_COLLECTION_SEALED_WAX]: MY_COLLECTION_SEALED_WAX_FIELDS,
      [TYPES.MY_COLLECTION_SEALED_WAX_SOLD]: MY_COLLECTION_SEALED_WAX_SOLD_FIELDS,
      [TYPES.MY_COLLECTION]: MY_COLLECTION_FIELDS,
      [TYPES.MY_COLLECTION_SOLD]: MY_COLLECTION_SOLD_FIELDS,
      [TYPES.API_CALL_LOGGING_FIELDS]: API_CALL_LOGGING_FIELDS,
      [TYPES.ADMIN_PLAYER_FIELDS]: ADMIN_PLAYER_FIELDS,
      [TYPES.ADMIN_SET_FIELDS]: ADMIN_SET_FIELDS,
      [TYPES.ADMIN_SET_VARIATION_FIELDS]: ADMIN_SET_VARIATION_FIELDS,
      [TYPES.ADMIN_GRADE_FIELDS]: ADMIN_GRADE_FIELDS,
      [TYPES.ADMIN_VARIATION_FIELDS]: ADMIN_VARIATION_FIELDS,
      [TYPES.ADMIN_CARD_FIELDS]: ADMIN_CARD_FIELDS,
      [TYPES.MY_COLLECTION_IMPORT_REJECTED]: MY_COLLECTION_IMPORT_REJECTED_FIELDS,
      [TYPES.ADMIN_CARD_RESULTS_FIELDS]: ADMIN_CARD_RESULTS_FIELDS,
    }[type] || AUCTION_FIELDS;

  let header = [];
  let rows = [];
  for (const field of fields) {
    header.push(field.label || field.key);
  }

  if (asyncDataFn) {
    data = await asyncDataFn(data);
  }

  if (!data.length && props.shouldHaltOnNoData) {
    return Toaster.WarningNotification("No results.");
  }

  data.map((result) => {
    let row = [];
    for (const field of fields) {
      let value = get(result, field.key);
      row.push(field.formatter ? field.formatter(value) + "" : value + "");
    }
    rows.push(row.join(delimiter).trim());
  });

  header = `${header.join(delimiter).trim()}\n`;
  rows = rows.join("\n");

  const contentType = "data:text/csv;encoding=utf-8,";
  const content = escapeFn(`${header}${rows}`);

  const a = document.createElement("a");
  a.href = encodeURI(`${contentType}${content}`);
  a.setAttribute("download", filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const escapeData = (str) => {
  return str.replace(/#/g, "").replace(/"/g, "'");
};

const DownloadCsvLink = ({
  data,
  buttonType = "link",
  asyncDataFn = null,
  type,
  filename,
  leaderBoardLink = false,
  expanded = false,
  delimiter = ",",
  escapeFn = escapeData,
  title = "Download CSV",
  ignoreDataLength = false,
  hideIcon = false,
  ...props
}) => {
  const [loading, setLoading] = useState(false);

  const download = async () => {
    setLoading(true);

    await downloadCsv({
      data,
      asyncDataFn,
      type,
      filename,
      delimiter,
      escapeFn,
      ...props,
    });

    setLoading(false);
  };

  if (data && (data.length || ignoreDataLength)) {
    if (loading) return <Spin />;
    if (leaderBoardLink || expanded) {
      return (
        <Button type={buttonType} onClick={download} className="download-csv">
          {title}
          {!hideIcon && <FileTextOutlined className="blue" style={{ marginLeft: ".25rem" }} />}
        </Button>
      );
    } else {
      return (
        <Tooltip trigger={isMobile ? "click" : "hover"} title="Download data">
          <Button
            onClick={(e) => {
              e.stopPropagation();
              download();
            }}
            type={buttonType}
            {...props}
            icon={!hideIcon && <FileTextOutlined className="blue" outlined="true" />}
          />
        </Tooltip>
      );
    }
  } else {
    return null;
  }
};

export default DownloadCsvLink;
